import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Sparkles, ClipboardList } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { CLUSTER_ICONS } from '../data/clusterIcons';
import { trackEvent } from '../utils/posthogClient';

export function StartPage() {
  const session = useAppStore((state) => state.session);
  const clusters = useAppStore((state) => state.dataModel.clusters);
  const showWhatToExpect = useAppStore(
    (state) => state.settings.featureFlags.startPage.whatToExpectCard,
  );
  const preparationStepEnabled = useAppStore(
    (state) => state.settings.featureFlags.preparationStep.enabled,
  );
  const analyticsEnabled = useAppStore((state) => state.settings.featureFlags.analytics.enabled);
  const startNewSession = useAppStore((state) => state.startNewSession);
  const discardSession = useAppStore((state) => state.discardSession);
  const navigate = useNavigate();
  const [confirmRestart, setConfirmRestart] = useState(false);

  const hasIncompleteSession = !!session && !session.result;
  const hasCompletedResult = !!session?.result;
  const firstStepPath = preparationStepEnabled ? '/vorbereitung' : '/kontext/angebot';

  function handleStart(): void {
    if (analyticsEnabled) trackEvent('test_start');
    startNewSession();
    void navigate(firstStepPath);
  }

  function handleResume(): void {
    if (analyticsEnabled) trackEvent('test_resumed');
    void navigate(session?.currentStep === 'result' ? '/ergebnis' : firstStepPath);
  }

  function handleViewResult(): void {
    void navigate('/ergebnis');
  }

  function handleRestartConfirmed(): void {
    if (analyticsEnabled) trackEvent('test_restart_confirmed', { source: 'start_page' });
    discardSession();
    setConfirmRestart(false);
    startNewSession();
    void navigate(firstStepPath);
  }

  return (
    <div className="page-container stack">
      <section className="stack">
        <h1>Ist Instagram wirklich der richtige Kanal für dein digitales Produkt?</h1>
        <p>
          Der Test bewertet Zielgruppe, Thema, Inhalte, Vertrauen, Sichtbarkeit, Alltag, Angebot,
          Verkauf und Unabhängigkeit.
        </p>
      </section>

      <div className="two-col-grid">
        <section className="card card--muted stack">
          <Sparkles className="card-icon" aria-hidden="true" />
          <p>
            Instagram ist vor allem ein Kanal für Aufmerksamkeit, Vertrauen und den ersten Schritt.
            Der eigentliche Verkauf benötigt meist einen klaren Weg außerhalb einzelner Beiträge.
          </p>
        </section>

        {showWhatToExpect && (
          <section className="card stack">
            <ClipboardList className="card-icon" aria-hidden="true" />
            <h2>Was dich erwartet</h2>
            <ul>
              <li>14 bewertete Fragen</li>
              <li>neun kurze Kontextfragen</li>
              <li>ungefähr 7 bis 12 Minuten</li>
              <li>ein Gesamt-Score</li>
              <li>sechs Teil-Ergebnisse</li>
              <li>Spannungsfelder</li>
              <li>konkrete nächste Schritte</li>
            </ul>
          </section>
        )}
      </div>

      <section className="stack">
        <h2>Sechs Bewertungsbereiche</h2>
        <div className="cluster-grid">
          {clusters.map((cluster) => {
            const Icon = CLUSTER_ICONS[cluster.id];
            return (
              <div key={cluster.id} className="card stack">
                <Icon className="card-icon" aria-hidden="true" />
                <h3>{cluster.title}</h3>
                <p>{cluster.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="stack" style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {hasCompletedResult && <Button onClick={handleViewResult}>Ergebnis erneut ansehen</Button>}
        {hasIncompleteSession && !hasCompletedResult && (
          <Button onClick={handleResume}>Test fortsetzen</Button>
        )}
        {(hasIncompleteSession || hasCompletedResult) && (
          <Button variant="secondary" onClick={() => setConfirmRestart(true)}>
            Neu beginnen
          </Button>
        )}
        {!hasIncompleteSession && !hasCompletedResult && (
          <Button onClick={handleStart}>Test starten</Button>
        )}
        <Link to="/ueber-den-test" className="button button--secondary">
          So funktioniert die Bewertung
        </Link>
      </section>

      <ConfirmDialog
        open={confirmRestart}
        title="Test neu beginnen?"
        description="Deine bisherigen Antworten in dieser Sitzung gehen dabei verloren."
        confirmLabel="Neu beginnen"
        onConfirm={handleRestartConfirmed}
        onCancel={() => setConfirmRestart(false)}
        danger
      />
    </div>
  );
}
